/**
 * "طباعة الروشتة" — print or save a prescription as PDF.
 *
 * Shared by the doctor's and the patient's prescription screens, because they
 * print the same letterhead and there is no reason for two copies of the
 * wiring. The patient could not print at all before this.
 *
 * The doctor record is what carries the letterhead — name, title, clinics,
 * logo, print colours. The doctor's own screen has it from the session; the
 * patient's does not, so it is looked up from the shared doctor list by the
 * prescription's `doctorId`.
 */

import { useState } from 'react';
import { Button } from './index';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { printPrescription } from '../../services/printPrescription';

export function PrintPrescriptionButton({
  prescription,
  doctor = null,
  patientName = '',
  patientCode = '',
  className = 'w-full',
  variant = 'ghost',
  label = 'طباعة الروشتة',
}) {
  const { doctors, findDoctor } = useData();
  const { error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);

  if (!prescription) return null;

  /** The prescribing doctor, however this screen can reach them. */
  const resolveDoctor = () => {
    if (doctor?.name) return doctor;

    const byId = prescription.doctorId ? findDoctor?.(prescription.doctorId) : null;
    if (byId) return byId;

    // `listForPatient` embeds the prescriber, which is all the patient screen
    // has when the doctor list has not loaded yet.
    const embedded = prescription.PrescribingDoctor;
    if (embedded?.name) {
      return {
        name: embedded.name,
        title: embedded.title || prescription.doctorTitle || '',
        clinics: [],
        logo: prescription.doctorLogo || '',
        image: embedded.image || '',
        printColors: undefined,
        prescriptionStyle: undefined,
      };
    }

    // Last resort: the snapshot the prescription itself stores.
    if (prescription.doctorName) {
      return {
        name: prescription.doctorName,
        title: prescription.doctorTitle || '',
        clinics: prescription.clinicName ? [{ name: prescription.clinicName }] : [],
        logo: prescription.doctorLogo || '',
        image: '',
      };
    }

    return null;
  };

  const handlePrint = async () => {
    const prescriber = resolveDoctor();

    if (!prescriber) {
      toastError(
        `تعذر العثور على بيانات الطبيب لهذه الروشتة${
          doctors?.length ? '' : ' — حدّث الصفحة وحاول مرة أخرى'
        }.`,
      );
      return;
    }

    setBusy(true);
    try {
      await printPrescription({
        prescription,
        doctor: prescriber,
        clinic:
          (prescriber.clinics || []).find(
            (c) =>
              c.name === prescription.clinicName ||
              String(c.id) === String(prescription.clinicId),
          ) || null,
        patientName: patientName || prescription.patientName,
        patientCode: patientCode || prescription.manualPatientCode,
      });
    } catch (err) {
      toastError(err.message || 'تعذر فتح صفحة الطباعة');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant={variant}
      icon="printer"
      loading={busy}
      onClick={handlePrint}
      className={className}
      title="اطبع الروشتة أو احفظها PDF من نافذة الطباعة"
    >
      {label}
    </Button>
  );
}

export default PrintPrescriptionButton;
